import { Box, Button, HStack, Icon, Spinner, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiBell } from "react-icons/fi";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  PortalLifecycleStatus,
  useGetPortalRegistrationStatusQuery,
  useRegisterPortalInterestMutation,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { currentPathSuffix } from "../../../../common/auth/returnTo";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppSelector } from "../../../../common/redux/hooks";
import { urls } from "../../../../urls";

export const RegisterInterestCta = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const authState = useAppSelector((state) => state.authState.state);
  const location = useLocation();
  const loggedInWithOrg = authState?.type === "LoggedInWithOrgContext";
  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;
  const displayer = useApiError();
  const [register, { isLoading }] = useRegisterPortalInterestMutation();
  const [justRegistered, setJustRegistered] = useState(false);
  // Server is the source of truth so the "following" state survives refresh/refetch.
  const { data: status, isLoading: statusLoading } = useGetPortalRegistrationStatusQuery(
    { eventId: event.id },
    { skip: !loggedInWithOrg || !isOpen }
  );

  // Only an open competition can be followed; the lifecycle banner explains other states.
  if (!isOpen) return null;

  const registered = justRegistered || (status?.hasRegistered ?? false);

  const handleRegister = async () => {
    try {
      await register({ eventId: event.id }).unwrap();
      setJustRegistered(true);
    } catch (error) {
      displayer.trigger(error);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="xl" px={{ base: "4", md: "6" }} py="4">
      <HStack spacing="4" justify="space-between" align="center" flexWrap="wrap" rowGap="3">
        <HStack spacing="3" align="center">
          <Icon as={FiBell} boxSize="5" color="smPrimary" flexShrink="0" />
          <Stack spacing="0">
            <Text fontWeight="semibold">{t("Want to participate in this procurement?")}</Text>
            <Text fontSize="sm" color="smMuted">
              {t("Register interest to follow updates from the buyer.")}
            </Text>
          </Stack>
        </HStack>
        {!loggedInWithOrg ? (
          <Button as={RouterLink} to={urls.loginWithReturnUrl(currentPathSuffix(location))} variant="outline" colorScheme="gray" flexShrink="0">
            {t("Log in to register interest")}
          </Button>
        ) : statusLoading && !justRegistered ? (
          <Spinner size="sm" color="smPrimary" flexShrink="0" />
        ) : registered ? (
          <Text fontWeight="semibold" color="smPrimary" flexShrink="0">
            {t("You are following this procurement")}
          </Text>
        ) : (
          <Button
            bg="smPrimary"
            color="white"
            _hover={{ opacity: 0.9 }}
            onClick={handleRegister}
            isLoading={isLoading}
            flexShrink="0"
          >
            {t("Register interest")}
          </Button>
        )}
      </HStack>
    </Box>
  );
};
